<template>
  <document
    :title="$t('message.invoice')"
    :header-items="headerItems"
    :from="invoice.from"
    :to="invoice.to"
    :table-label="$t('message.invoiceItems')"
    :sub-total="invoice.subTotal.displayValue"
    :vat-total="invoice.vatTotal.displayValue"
    :total="invoice.total.displayValue"
    show-add-table-row-button
    editable-terms
    editable-notes
    @terms-change="terms => (invoice.terms = terms)"
    @notes-change="notes => (invoice.notes = notes)"
    @add-table-row-click="addRow"
  >
    <template slot="table">
      <b-table :items="invoice.items" :fields="fields" stacked="md">
        <template v-slot:cell(removeItem)="data">
          <n-button
            @click.native="deleteRow(data.index)"
            class="remove d-none d-md-inline-block"
            type="danger"
            size="sm"
            :alt="$t('message.removeItem')"
            round
            icon
          >
            <i class="fa fa-times" />
          </n-button>
          <n-button
            @click.native="deleteRow(data.index)"
            class="remove d-inline-block d-md-none"
            type="danger"
            :alt="$t('message.removeItem')"
            round
            >{{ $t("message.removeItem") }}</n-button
          >
        </template>
        <template v-slot:cell(description)="data">
          <ValidationProvider rules="required">
            <vue-bootstrap-typeahead
              class="w-100"
              input-class="invoiceForm__noBorder"
              v-model="data.item.description"
              :data="itemHistoryQueryData"
              placeholder="Enter an item description"
              @hit="item => handlePreviousItemSelected(item, data)"
            />
          </ValidationProvider>
        </template>
        <template v-slot:cell(price)="data">
          <ValidationProvider rules="required|min_value:0">
            <b-form-input
              class="invoiceForm__noBorder"
              placeholder="0"
              v-model="data.item.price"
              type="number"
            />
          </ValidationProvider>
        </template>
        <template v-slot:cell(quantity)="data">
          <ValidationProvider rules="required|min_value:1">
            <b-form-input
              class="invoiceForm__noBorder"
              placeholder="0"
              v-model="data.item.quantity"
              type="number"
              min="0"
            />
          </ValidationProvider>
        </template>
        <template v-slot:cell(vat)="data">
          <ValidationProvider rules="required|min_value:0">
            <b-form-input
              class="invoiceForm__noBorder"
              placeholder="0"
              v-model="data.item.vat"
              type="number"
              min="0"
              step="0.1"
            />
          </ValidationProvider>
        </template>
      </b-table>
    </template>
  </document>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";
import { formatMoney, dateToHuman } from "@/helpers/helpers.js";
import { DEFAULT_INVOICE } from "@/store/accounting/invoices";
import { Document } from "@/components";

export default {
  components: {
    Document
  },
  props: {
    invoice: {
      type: Object,
      default: () => DEFAULT_INVOICE
    },
    recurring: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      fields: [
        {
          key: "removeItem",
          label: "",
          class: "document__table__remove"
        },
        {
          key: "description",
          label: this.$t("message.invoiceItem"),
          class: "document__table__description"
        },
        {
          key: "price",
          label: this.$t("message.invoiceItemUnitCost"),
          class: "document__table__price"
        },
        {
          key: "quantity",
          label: this.$t("message.invoiceItemQuantity"),
          class: "document__table__quantity"
        },
        {
          key: "vat",
          label: this.$t("message.invoiceItemVat"),
          class: "document__table__vat"
        },
        {
          key: "total",
          label: this.$t("message.invoiceItemPrice"),
          class: "document__table__total",
          formatter: (value, key, item) =>
            formatMoney(item.price * item.quantity)
        }
      ]
    };
  },
  methods: {
    formatMoney,
    ...mapActions([
      "fetchInvoiceItemHistory",
      "applyCustomerInvoiceItemHistory",
      "updateInvoiceLogo"
    ]),
    addRow() {
      const { items } = this.invoice;
      const numItems = items.length;
      const latestItem = numItems > 0 ? items[numItems - 1] : null;
      const vat =
        (latestItem && latestItem.vat) ||
        (this.userSettings.invoicing &&
          this.userSettings.invoicing.defaultVatRate) ||
        "";

      this.invoice.items.push({
        description: "",
        price: "",
        quantity: "1",
        vat
      });
    },
    deleteRow(index) {
      this.invoice.items.splice(index, 1);
    },
    handlePreviousItemSelected(item, row) {
      const { price } = this.applicableInvoiceItemHistory[item];
      row.price = price;
    }
  },
  computed: {
    ...mapGetters([
      "userSettings",
      "invoiceLogoFile",
      "applicableInvoiceItemHistory"
    ]),
    itemHistoryQueryData() {
      return Object.keys(this.applicableInvoiceItemHistory);
    },
    headerItems() {
      return [
        {
          label: this.$t("message.invoiceNumber"),
          value: this.invoice.number
        },
        {
          label: this.$t("message.issuedDate"),
          value: dateToHuman(this.invoice.sentDate)
        },
        {
          label: this.$t("message.dueDate"),
          value: dateToHuman(this.invoice.dueDate)
        }
      ];
    },
    subTotal() {
      return this.invoice.items.reduce(
        (total, item) => total + item.quantity * item.price,
        0
      );
    },
    vatTotal() {
      return this.invoice.items.reduce(
        (total, item) => total + item.quantity * item.price * (item.vat / 100),
        0
      );
    },
    total() {
      return this.subTotal + this.vatTotal;
    }
  },
  filters: {
    dateToHuman
  },
  created() {
    this.fetchInvoiceItemHistory();
  },
  watch: {
    "invoice.to"(value) {
      if (!value) return;
      if (value === this.invoice.to) return;

      this.applyCustomerInvoiceItemHistory(value._id);

      const applicableInvoiceItemHistory = this.applicableInvoiceItemHistory;

      this.invoice.items = this.invoice.items.map(item => {
        item.price = _.get(
          applicableInvoiceItemHistory,
          `[${item.description}].price`,
          item.price
        );
        return item;
      });
    },
    subTotal(value) {
      this.invoice.subTotal.amount = value;
      this.invoice.subTotal.displayValue = formatMoney(value);
    },
    vatTotal(value) {
      this.invoice.vatTotal.amount = value;
      this.invoice.vatTotal.displayValue = formatMoney(value);
    },
    total(value) {
      this.invoice.total.amount = value;
      this.invoice.total.displayValue = formatMoney(value);
    },
    invoice(value) {
      this.$emit("change", value);
    }
  }
};
</script>
<style lang="scss">
.invoiceForm__noBorder {
  border: none !important;
  &:focus {
    border: none !important;
  }
}
</style>
